import { HandlerEvent } from 'post-robot';
import { isPopupBrowser } from 'common/browser';
/* START GENAI@GHCOPILOT */
import { pushErrors, pushInit } from 'common/gamp';
import {
  initLogRocket,
  setCollectHeaders,
  setCollectPayload,
  setCorrelationId
} from 'common/logrocket';
/* END GENAI@GHCOPILOT */
import { CommunicatorState, saveInitData, saveMeta } from 'communicator/store';
import { getDcfExperience, getFeatureToggles, getIsVisaSRCi } from 'communicator/store/selectors';
import { hasValidErmCookie, hasValidSsiCookie } from 'communicator/utils/cookies';
import createCorrelationId from 'communicator/utils/correlation-id';
import { initMeasure, markStart } from 'communicator/utils/instrumentation';
import { calculateSplunkLoggingEnabled } from 'communicator/utils/splunk';
import { sendToVisaSdk } from 'communicator/utils/visa-sdk';
import { InitRequest } from 'types/visa-sdk/init';

export default async function init(event: HandlerEvent<InitRequest>) {
  try {
    markStart('INIT_COMPLETE');
    const stime = Date.now();
    const initData = event.data.data;
    const metadata = event.data.meta;

    saveInitData(initData);

    const isIntegratedExperience = getDcfExperience() === 'integrated';

    if (!isPopupBrowser(isIntegratedExperience)) {
      // for integrated v2 chrome flows, use the dot loader with merchantName
      const merchantName = isIntegratedExperience
        ? initData.dpaData?.dpaPresentationName || 'Merchant'
        : undefined;

      event.data.createLoader(merchantName);
    }

    /* START GENAI@GHCOPILOT */
    const metaUpdates: Partial<CommunicatorState['meta']> = {
      correlationId:
        (initData.customData?.correlationId || initData.srciTransactionId) ?? createCorrelationId(),
      init: true,
      integrationType: initData.dpaTransactionOptions?.customInputData?.isVsbButtonless
        ? 'vsb-buttonless'
        : initData.customData?.isVsb
          ? 'vsb'
          : initData.customData
            ? 'visa-srci'
            : 'psp',
      isBiometricsEnabledDevice: metadata && metadata.isBiometricsEnabledDevice,
      isSplunkLoggingEnabled:
        typeof initData.customData?.isSplunkLoggingEnabled === 'boolean'
          ? initData.customData.isSplunkLoggingEnabled
          : calculateSplunkLoggingEnabled()
    };
    /* END GENAI@GHCOPILOT */

    // For Visa SRCi, use sessionId from Merchant SDK if present in customData
    if (getIsVisaSRCi() && initData.customData?.sessionId) {
      metaUpdates.sessionId = initData.customData.sessionId;
    }

    saveMeta(metaUpdates);

    /* START GENAI@GHCOPILOT */
    if (getFeatureToggles().enableLogrocket) {
      setCorrelationId(metaUpdates.correlationId as string, 'communicator');
      setCollectHeaders(Boolean(getFeatureToggles().enableLogrocketCollectHeaders));
      setCollectPayload(Boolean(getFeatureToggles().enableLogrocketCollectPayload));
      initLogRocket('communicator');
    }
    /* END GENAI@GHCOPILOT */

    sendToVisaSdk('visa-communicator:internal:cr', {
      correlationId: metaUpdates.correlationId
    });

    // START GENAI@GHCOPILOT
    // TLPERF1-6342: induce 1s delay for init - in addition to 1s at src-orchestrator to give 50th percentile THM profile complete enough time
    if (
      getFeatureToggles().isRecognizedDelayForThm &&
      (hasValidErmCookie() || hasValidSsiCookie())
    ) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    // END GENAI@GHCOPILOT

    initMeasure(stime, Date.now());
    pushInit(initData, Date.now() - stime);
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    pushErrors('Init Error', err);
    // END GENAI@GHCOPILOT
  }
}
